import React, { useState } from 'react';
import {
  Box, Chip, CircularProgress, Divider, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { outcomesApi } from '../../api/services';
import type { DigestOutcomeLine, DigestOutcomeRow } from '../../api/services';
import { CVD_BLUE, CVD_ORANGE } from '../../utils/cvdPalette';

// Digest Outcomes (sc-6064): per sent restock digest, HOW the customer acted.
// Design settled 2026-07-23 (mockup: docs/specs/2026-07-23-sc6064-digest-
// outcomes-mockup.html): acceptance-mix bars + per-digest table + most-omitted
// rollup. Color = POLARITY, not class — blue is reserved for accepted-as-is
// (the outcome we want), orange marks every deviation worth a look, neutral
// gray is no-action (absence). Identity always lives in the label text: six+
// classes can't be told apart by color alone under the CVD constraint.
// Read-time endpoint — nothing cached in the review payload, so class-rule
// revisions stay frontend-visible immediately and reclassify all history.

const NO_ACTION_GRAY = '#6f7780';
// Text-legible amber for dates/flags (bars keep CVD_ORANGE; #e69f00 is too
// low-contrast for text on white).
const AMBER_TEXT = '#8a6100';

// Hover tint derives from CVD_BLUE so a palette change keeps its one edit point.
const hexToRgba = (hex: string, alpha: number): string => {
  const n = parseInt(hex.slice(1), 16);
  // eslint-disable-next-line no-bitwise
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};
const HOVER_TINT = hexToRgba(CVD_BLUE, 0.14);

// Exported for the index's digest-column tooltips (sc-6100): one label/order
// source, so the fleet view and this section can't drift.
export const CLASS_ORDER = [
  'accepted_as_is', 'accepted_indirect', 'accepted_with_additions',
  'quantity_reduced', 'cherry_picked', 'late_action', 'no_action',
] as const;

export const CLASS_LABEL: Record<string, string> = {
  accepted_as_is: 'Accepted as-is',
  accepted_indirect: 'Accepted indirect',
  accepted_with_additions: 'Accepted + additions',
  quantity_reduced: 'Reduced quantity',
  cherry_picked: 'Cherry-picked',
  late_action: 'Late action',
  no_action: 'No action',
};

type Tier = 'good' | 'review' | 'none';
const classTier = (outcome: string): Tier =>
  outcome === 'accepted_as_is' ? 'good' : outcome === 'no_action' ? 'none' : 'review';

const TIER_BAR: Record<Tier, string> = { good: CVD_BLUE, review: CVD_ORANGE, none: NO_ACTION_GRAY };
const TIER_CHIP: Record<Tier, { bg: string; fg: string }> = {
  good: { bg: '#e3eef7', fg: '#1d5a8a' },
  review: { bg: '#fdf3dd', fg: AMBER_TEXT },
  none: { bg: '#eceef0', fg: '#555' },
};

// Bare box-grain quantity ("×4", "3 of 5") — whole numbers stay whole, member
// SKU conversions may leave one decimal.
const fmtQty = (n: number): string => (Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10));

const CENTRAL = 'America/Chicago';
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: CENTRAL });
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: CENTRAL });
// Date-ONLY strings (`since`) are calendar dates, not instants — routing them
// through the Central formatter would parse as UTC midnight and render a day
// early. Construct as local y/m/d instead.
const fmtDay = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const Dash: React.FC = () => <Typography variant="body2" color="text.disabled">—</Typography>;

// Send/response timestamp: time over date, Central (matching the server-side
// business-day semantics — a browser-local render could contradict the
// same-day coloring at edge hours).
const TimeStamp: React.FC<{ iso: string; dateColor?: string }> = ({ iso, dateColor }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block" whiteSpace="nowrap">
      {fmtTime(iso)}
    </Typography>
    <Typography variant="body2" whiteSpace="nowrap" sx={dateColor ? { color: dateColor } : undefined}>
      {fmtDate(iso)}
    </Typography>
  </Box>
);

const ClassChip: React.FC<{ outcome: string }> = ({ outcome }) => {
  const tier = TIER_CHIP[classTier(outcome)];
  return (
    <Chip
      label={CLASS_LABEL[outcome] ?? outcome}
      size="small"
      sx={{ bgcolor: tier.bg, color: tier.fg, fontWeight: 500 }}
    />
  );
};

interface HoverProps {
  hoverSku: string | null;
  setHoverSku: (sku: string | null) => void;
}

// One SKU per line; hovering highlights every instance of that SKU across the
// table and the most-omitted list, so per-SKU patterns pop ("they omit
// 96120CATCH every time").
const SkuLine: React.FC<HoverProps & { sku: string; text: string; tooltip?: string; color?: string }> = (
  { sku, text, tooltip, color, hoverSku, setHoverSku },
) => {
  const line = (
    <Typography
      variant="body2"
      whiteSpace="nowrap"
      onMouseEnter={() => setHoverSku(sku)}
      onMouseLeave={() => setHoverSku(null)}
      sx={{
        fontSize: 13,
        color,
        px: 0.5,
        mx: -0.5,
        borderRadius: 0.5,
        bgcolor: hoverSku === sku ? HOVER_TINT : undefined,
      }}
    >
      {text}
    </Typography>
  );
  return tooltip ? <Tooltip title={tooltip} placement="top">{line}</Tooltip> : line;
};

const unitsTooltip = (l: DigestOutcomeLine): string =>
  l.rec_units != null ? `${l.units} of ${l.rec_units} units` : `${l.units} units`;

const LineList: React.FC<{ lines: React.ReactNode[] }> = ({ lines }) =>
  lines.length ? <Box>{lines}</Box> : <Dash />;

const DigestRow: React.FC<HoverProps & { row: DigestOutcomeRow }> = ({ row, hoverSku, setHoverSku }) => {
  const hover = { hoverSku, setHoverSku };
  const overFlags = row.accepted.filter((l) => l.over_boxes != null);
  return (
    <TableRow>
      <TableCell><TimeStamp iso={row.sent_at} /></TableCell>
      <TableCell>
        {row.accepted_at ? (
          <TimeStamp iso={row.accepted_at} dateColor={row.accepted_same_day ? CVD_BLUE : AMBER_TEXT} />
        ) : <Dash />}
      </TableCell>
      <TableCell><ClassChip outcome={row.outcome} /></TableCell>
      <TableCell>
        <LineList lines={row.accepted.map((l) => (
          <SkuLine key={l.sku} {...hover} sku={l.sku} text={`${l.sku} ×${fmtQty(l.boxes)}`} tooltip={unitsTooltip(l)} />
        ))} />
      </TableCell>
      <TableCell>
        <LineList lines={row.reduced.map((l) => (
          <SkuLine
            key={l.sku} {...hover} sku={l.sku}
            text={`${l.sku} ${fmtQty(l.boxes)} of ${fmtQty(l.rec_boxes!)}`}
            tooltip={unitsTooltip(l)}
          />
        ))} />
      </TableCell>
      <TableCell>
        <LineList lines={row.omitted.map((l) => (
          <SkuLine key={l.sku} {...hover} sku={l.sku} text={`${l.sku} ×${fmtQty(l.boxes)}`} tooltip={unitsTooltip(l)} />
        ))} />
      </TableCell>
      <TableCell>
        <LineList lines={[
          ...row.extras.map((e) => (
            <SkuLine key={`x-${e.sku}`} {...hover} sku={e.sku} text={`${e.sku} ×${e.quantity}`} color={AMBER_TEXT} />
          )),
          ...overFlags.map((l) => (
            <SkuLine
              key={`o-${l.sku}`} {...hover} sku={l.sku}
              text={`${l.sku} +${fmtQty(l.over_boxes!)} over rec`} color={AMBER_TEXT}
            />
          )),
        ]} />
      </TableCell>
      <TableCell>
        {row.orders.length ? row.orders.map((o) => (
          <Box key={o.id}>
            <Tooltip title={`${fmtTime(o.at)} ${fmtDate(o.at)}${o.url ? ' — open in HexaHQ' : ''}`} placement="top">
              {o.url ? (
                <a
                  href={o.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: CVD_BLUE, textDecoration: 'none', fontSize: 13, whiteSpace: 'nowrap' }}
                >
                  #{o.id}
                </a>
              ) : (
                <Typography component="span" variant="body2" sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                  #{o.id}
                </Typography>
              )}
            </Tooltip>
          </Box>
        )) : <Dash />}
      </TableCell>
    </TableRow>
  );
};

const StateNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
    <Typography variant="body2" color="text.secondary">{children}</Typography>
  </Paper>
);

export const DigestOutcomes: React.FC<{ locationId: number }> = ({ locationId }) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['outcomes', 'digest-outcomes', locationId],
    queryFn: () => outcomesApi.digestOutcomes(locationId),
    staleTime: 5 * 60 * 1000,
  });
  const [hoverSku, setHoverSku] = useState<string | null>(null);
  const hover = { hoverSku, setHoverSku };

  if (isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (isError) {
    return (
      <Typography variant="body2" color="error" sx={{ p: 1 }}>
        Couldn’t load digest outcomes — try again.
      </Typography>
    );
  }
  if (!data) return null;
  if (!data.account_linked) {
    return <StateNote>Location isn’t linked to an HQ account — digest outcomes unavailable.</StateNote>;
  }
  if (!data.order_data_available) {
    return <StateNote>HQ order data unavailable — digest outcomes can’t be computed right now.</StateNote>;
  }

  const total = data.digests.length;
  if (total === 0) {
    return <StateNote>No restock digests sent to this location since {fmtDay(data.since)}.</StateNote>;
  }
  const maxCount = Math.max(1, ...CLASS_ORDER.map((c) => data.summary[c] ?? 0));

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        How this location responded to each sent restock digest · since {fmtDay(data.since)} · {total} digest{total === 1 ? '' : 's'} sent
      </Typography>

      {/* Acceptance mix: one labeled bar per class (count · share). A stacked
          bar would need 7 distinguishable colors — identity lives in the row
          labels instead, color carries polarity only. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        {CLASS_ORDER.map((klass) => {
          const count = data.summary[klass] ?? 0;
          const tier = classTier(klass);
          return (
            <React.Fragment key={klass}>
              {klass === 'accepted_indirect' && <Divider sx={{ my: 1 }} />}
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ my: 0.5 }}>
                <Typography variant="body2" sx={{ width: 170, textAlign: 'right', flexShrink: 0 }}>
                  {CLASS_LABEL[klass]}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{
                    width: `${(count / maxCount) * 100}%`,
                    height: 16,
                    bgcolor: TIER_BAR[tier],
                    borderRadius: '0 4px 4px 0',
                  }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ width: 76, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {count} · {Math.round((count / total) * 100)}%
                </Typography>
              </Stack>
            </React.Fragment>
          );
        })}
      </Paper>

      {/* Per-digest table (newest first). Timestamps render Central to match
          the server's business-day semantics. */}
      <Paper variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
        <Table size="small" sx={{ '& th': { textAlign: 'center', color: 'text.secondary' }, '& td': { verticalAlign: 'middle' } }}>
          <TableHead>
            <TableRow>
              <TableCell>Digest</TableCell>
              <TableCell>Accepted</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>SKUs Accepted</TableCell>
              <TableCell>Reduced</TableCell>
              <TableCell>Omitted</TableCell>
              <TableCell>Extras</TableCell>
              <TableCell>Order(s)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.digests.map((row) => (
              <DigestRow key={row.digest_id} row={row} {...hover} />
            ))}
          </TableBody>
        </Table>
      </Paper>

      {data.most_omitted.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Most omitted</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            SKUs most often omitted or reduced when recommended — where the rec isn’t landing
          </Typography>
          <Paper variant="outlined" sx={{ maxWidth: 560 }}>
            <Table size="small" sx={{ '& th': { textAlign: 'center', color: 'text.secondary' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>SKU / group</TableCell>
                  <TableCell align="right">Recommended</TableCell>
                  <TableCell align="right">Omitted</TableCell>
                  <TableCell align="right">Reduced</TableCell>
                  <TableCell align="right">Miss rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.most_omitted.map((s) => (
                  <TableRow key={s.sku}>
                    <TableCell>
                      <SkuLine {...hover} sku={s.sku} text={s.sku} />
                    </TableCell>
                    <TableCell align="right">{s.recommended}×</TableCell>
                    <TableCell align="right">{s.omitted}</TableCell>
                    <TableCell align="right">{s.reduced}</TableCell>
                    <TableCell align="right">{Math.round(s.miss_rate * 100)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
};
